import { FC, useState } from 'react';
import { Button, Modal, Segmented, Space, Timeline, Typography } from 'antd';
import styled from 'styled-components';
import { useBillingPlansContext } from '@/entities/billing-plan';
import { useNotificationContext } from '@/features/notifications';
import {
  PlanResponse,
  SubscriptionChangePreviewResponse,
  useGetSubscriptionPriceChangePreview,
  useHandleSubscription,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { Col, Row } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

type Props = {
  open: boolean;
  onSuccess: () => void;
  onCancel: () => void;
  price: PlanResponse['price'];
};

const { Title, Paragraph } = Typography;

export const ProPlanMonthlyDowngradeModal: FC<Props> = ({
  open,
  onSuccess,
  onCancel,
  price,
}) => {
  const { activeOrganization } = useRouterContext();
  const { showNotification } = useNotificationContext();
  const { refetchPlans } = useBillingPlansContext();

  const [isLoading, setIsLoading] = useState(false);

  const { data: previewData, isLoading: previewDataLoading } =
    useGetSubscriptionPriceChangePreview<SubscriptionChangePreviewResponse>(
      {
        pathParams: {
          organizationId: activeOrganization?.id!,
          planType: 'Pro',
          interval: 'Monthly',
        },
      },
      {
        enabled: Boolean(activeOrganization?.id && open),
        onError(err) {
          captureError(err, {
            module:
              'pro_plan_monthly_downgrade_modal_on_error_get_subscription_price_change_preview',
            action: 'get_subscription_price_change_preview',
          });
        },
      },
    );

  const { refetch: downgradeToProPlan } = useHandleSubscription(
    {
      pathParams: {
        planType: 'Pro',
        organizationId: activeOrganization?.id!,
        interval: 'Monthly',
      },
    },
    {
      enabled: false,
      onSuccess() {
        open && onDowngradeSuccess();
      },
    },
  );

  const handlePlanDowngrade = () => {
    setIsLoading(true);
    downgradeToProPlan();
  };

  const onDowngradeSuccess = () => {
    showNotification({
      message: 'Plan change successful',
      description: 'You have successfully changed your plan to Pro.',
      type: 'success',
    });
    refetchPlans();
    setIsLoading(false);
    onSuccess();
  };

  return (
    <Modal
      title={
        <Col
          alignItems="flex-start"
          style={{ margin: '10px 0', width: '100%' }}
        >
          <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
            Downgrade to Pro plan
          </Title>
        </Col>
      }
      destroyOnClose
      width={450}
      open={open}
      footer={null}
      style={{ maxWidth: '90%' }}
      onCancel={onCancel}
    >
      <Paragraph style={{ marginBottom: 20 }}>
        You are about to Downgrade to Pro plan.
      </Paragraph>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 40 }}>
        <div>Billing period:</div>
        <Segmented
          value="Monthly"
          options={['Monthly']}
          defaultValue="Monthly"
        />
      </Space>

      {previewData && (
        <>
          <Timeline
            items={[
              {
                children: (
                  <Row justifyContent="space-between">
                    <div>Today</div>
                    <div>
                      {previewData.totalBilledNow <= 0
                        ? '$0'
                        : `$${~~(previewData.totalBilledNow / 100)}`}
                    </div>
                  </Row>
                ),
              },
              {
                children: (
                  <Row justifyContent="space-between">
                    <div>
                      {new Date(previewData?.nextInvoiceDate!)
                        .toDateString()
                        .substring(4)}
                    </div>
                    <div>{`$${~~(previewData.nextInvoiceAmount / 100)}`}</div>
                  </Row>
                ),
              },
            ]}
            style={{ fontWeight: 600 }}
          />
          <Description>
            You can cancel your subscription at any time.
          </Description>
          <Paragraph style={{ fontWeight: 600 }}>
            After{' '}
            {new Date(previewData?.nextInvoiceDate!)
              .toDateString()
              .substring(4)}
            , you will be charged $
            {price.find((p) => p.billingInterval === 'Monthly')!.value}{' '}
            automatically every month until you cancel.
          </Paragraph>
          <Paragraph>Do you want to continue?</Paragraph>
        </>
      )}
      <Row justifyContent="flex-end" gap={15} style={{ marginTop: 30 }}>
        <Button size="large" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          size="large"
          loading={isLoading || previewDataLoading}
          onClick={handlePlanDowngrade}
        >
          Yes, downgrade to Pro
        </Button>
      </Row>
    </Modal>
  );
};

const Description = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 20px;
  margin-top: -30px;
`;
